import React, { useEffect, useRef } from 'react';

const BestPracticesModal = ({ isOpen, onClose, animationUrl }) => {
  const contentRef = useRef(null);
  
  // Check if scrollbar is needed and update class accordingly
  useEffect(() => {
    if (contentRef.current) {
      const hasOverflow = contentRef.current.scrollHeight > contentRef.current.clientHeight;
      if (hasOverflow) {
        contentRef.current.classList.add('has-scrollbar');
      } else {
        contentRef.current.classList.remove('has-scrollbar');
      }
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-80 z-[999999]">
      <div className="bg-black rounded-lg w-[95%] sm:w-[85%] md:w-[75%] lg:w-[60%] xl:w-[50%] max-w-3xl max-h-[95vh] flex flex-col relative">
        {/* Close button */}
        <button 
          onClick={onClose} 
          className="absolute top-3 right-3 text-white hover:text-[#EAECEF] bg-black bg-opacity-60 p-2 rounded-full z-10"
          aria-label="Close"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>

        {/* Video Animation */}
        <div className="w-full h-auto max-h-[30vh] overflow-hidden">
          <video 
            className="w-full object-cover" 
            autoPlay 
            loop 
            muted 
            playsInline
          >
            <source src={animationUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>

        {/* Content */}
        <div 
          ref={contentRef}
          className="p-6 overflow-y-auto flex-grow scrollbar scrollbar-thumb-[#178A94] scrollbar-track-[#2C2F30] scrollbar-thin"
        >
          <h2 className="text-2xl font-bold text-[#EAECEF] mb-4">
            🚀 Welcome to Lucid TTS!
          </h2>

          <div className="space-y-4 text-[#B0B4BB]">
            <p className="text-[#EAECEF] text-lg">
              Enhance your audience experience with our powerful AI voice engine.
            </p>
            
            <h3 className="text-lg font-semibold text-[#EAECEF] mt-4">Best Practices</h3>
            <ul className="list-disc pl-5 space-y-3">
              <li>Expand abbreviations into full words unless commonly spoken.  
                <br />Example: "GPT" → "జిపిటి"</li>
              
              <li>Convert numbers, dates, and time into words.  
                <br />Example: "123" → "నూటా ఇరవై మూడు", "15-08-1947" → "పదిహేను ఆగస్టు నైన్టీ ఫోర్టీ సెవన్", "10:30 AM" → "పది ముప్పై ఉదయం"</li>
              
              <li>Handle punctuation and formatting properly.  
                <br />Use commas (,) and double quotes (" ") correctly. Avoid excessive dots (...).  
                <br />Remove extra symbols, emojis, and text inside brackets ( ) or [ ].</li>
              
              <li>Convert web & tech terms for clarity.  
                <br />Example: "గూగల్.కామ్" → "గూగల్ డాట్ కామ్", "₹100" → "రూపాయలు వంద", "C++" → "సీ ప్లస్ ప్లస్"</li>
              
              <li>Add pauses for natural speech.  
                <br />Use pauses (||0.5s||, ||1s||, ||2s||) in long sentences.  
                <br />Example: "ఈ రోజు నేను ఒక గొప్ప పుస్తకం చదువుతున్నాను. ఇది నాకు చాలా ఆసక్తిగా ఉంది. 
                పుస్తకం కథలు మరియు పాఠాలను అందించడంలో ఎంతో ప్రత్యేకమైనది. ||1.1s|| నేను దీన్ని పూర్తిగా చదివేందుకు ఎదురు చూస్తున్నాను."</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-[#2C2F30] bg-black">
          <div className="flex justify-between items-center">
            <a 
              href="https://dreamsync.gitbook.io/lucidtts/"
              target="_blank"
              rel="noopener noreferrer" 
              className="text-[#178A94] hover:text-[#146E75] font-medium"
            >
              Learn More
            </a>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-[#178A94] hover:bg-[#146E75] text-white rounded-lg font-medium"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BestPracticesModal;
